// @flow

import * as wjcCore from 'wijmo/wijmo';
import * as wjcGrid from 'wijmo/wijmo.grid';

type Options = {
  onColFreezed?: number => mixed,
  frozenColumns?: number,
};

export default class FreezeBarColumns {
  flex: wjcGrid.FlexGrid;
  host: HTMLElement;
  dragging = false;
  freezeBar: HTMLElement;
  freeze = -1;
  onColFreezed: ?(number) => mixed;
  frozenColumns: ?number;
  firstRun: ?boolean;
  mouseUpHandler: any => mixed;
  mouseLeaveHandler: any => mixed;

  constructor(options: Options) {
    const { onColFreezed, frozenColumns } = options;

    this.onColFreezed = onColFreezed;
    this.frozenColumns = frozenColumns;
  }

  componentWillUnmount = () => {
    if (
      this.freezeBar &&
      this.freezeBar.parentElement &&
      typeof this.freezeBar.parentElement.removeChild === 'function'
    ) {
      this.freezeBar.parentElement.removeChild(this.freezeBar);
    }

    if (this.flex && this.flex.columns && this.flex.columns.collectionChanged) {
      this.flex.columns.collectionChanged.removeAllHandlers();
    }

    if (this.host && typeof this.host.removeEventListener === 'function') {
      this.host.removeEventListener('mousedown', this.gridMouseDown);
      this.host.removeEventListener('mousemove', this.gridMouseMove);
      this.host.removeEventListener('mouseup', this.mouseUpHandler);
      this.host.removeEventListener('mouseleave', this.mouseLeaveHandler);
    }
  };

  // add a freezing handle to a FlexGrid
  addFreezeBar(flex) {
    // create freeze handle
    const freezeBar = document.createElement('div');
    const host = flex.hostElement;

    this.dragging = false;
    this.flex = flex;
    this.flex.frozenColumns = this.frozenColumns || 0;
    this.host = host;
    this.freezeBar = freezeBar;

    wjcCore.setCss(freezeBar, {
      cursor: 'col-resize',
      backgroundColor: '#80ADBF',
      opacity: 0.5,
      zIndex: 5,
      position: 'absolute',
      width: 4,
      top: 0,
      bottom: 0,
    });

    try {
      // add handle to flex
      const root = host.querySelector('[wj-part="root"]');
      root.parentElement.appendChild(freezeBar);
      this.positionfreezeBar(flex, freezeBar, flex.frozenColumns);

      // attach event handlers
      flex.columns.collectionChanged.addHandler(() => {
        this.positionfreezeBar(flex, freezeBar, flex.frozenColumns);
      });
      host.addEventListener('mousedown', this.gridMouseDown, true);
      host.addEventListener('mousemove', this.gridMouseMove, true);

      this.mouseUpHandler = e => {
        this.stopDragging(e);
      };

      this.mouseLeaveHandler = e => {
        if (e.target === host) {
          this.stopDragging(e);
        }
      };

      // stop dragging when the user releases the mouse button
      host.addEventListener('mouseup', this.mouseUpHandler, true);

      // stop dragging when the mouse leaves the control
      host.addEventListener('mouseleave', this.mouseLeaveHandler, true);
    } catch (e) {}
  }

  repositionFreezeBar = () => {
    this.positionfreezeBar(this.flex, this.freezeBar, this.flex.frozenColumns);
  };

  gridMouseDown = (e: MouseEvent) => {
    const el = document.elementFromPoint(e.clientX, e.clientY);

    if (
      el === this.freezeBar &&
      this.flex.rows.length > 0 &&
      this.flex.columns.length > 0
    ) {
      // prepare to freeze
      this.flex.frozenColumns = 0;
      this.flex.scrollPosition = new wjcCore.Point(
        0,
        this.flex.scrollPosition.y,
      );
      this.dragging = true;
      this.freeze = -1;
      this.host.style.cursor = 'col-resize';

      // done with this event
      e.preventDefault();
      e.stopPropagation();
    }
  };

  gridMouseMove = (e: MouseEvent) => {
    if (this.dragging) {
      // find closest edge
      let minDist = null;

      // eslint-disable-next-line
      for (let i = 0; i <= this.flex.viewRange.col2; i++) {
        const rc = this.flex.getCellBoundingRect(0, i);
        const dist = Math.abs(rc.left - e.clientX);

        if (minDist == null || dist < minDist) {
          minDist = dist;
          this.freeze = i;

          if (
            this.frozenColumns !== this.freeze &&
            typeof this.onColFreezed === 'function'
          ) {
            this.onColFreezed(this.freeze);
          }
        }
      }

      // move element to edge
      const forcePosition = this.freeze === 0;
      this.positionfreezeBar(
        this.flex,
        this.freezeBar,
        this.freeze,
        forcePosition,
      );

      // done with this event
      e.preventDefault();
      e.stopPropagation();
    }
  };

  // stop dragging when the button is released or when the mouse leaves the control
  stopDragging(e: any) {
    if (this.dragging) {
      this.dragging = false;
      if (this.freeze > -1) {
        this.flex.frozenColumns = this.freeze;
      }
      this.host.style.cursor = '';
      e.preventDefault();
      e.stopPropagation();
    } else {
      this.positionfreezeBar(
        this.flex,
        this.freezeBar,
        this.flex.frozenColumns,
      );
    }
  }

  resetFreezeBar = (defaultCol: number = 0) => {
    this.flex.frozenColumns = defaultCol;

    this.positionfreezeBar(
      this.flex,
      this.freezeBar,
      this.flex.frozenColumns,
      true,
    );
  };

  // position the freeze bar on the right of the last frozen column
  positionfreezeBar(
    flex,
    div: HTMLElement,
    frozen: number,
    force?: boolean,
  ) {
    let left = -10;
    if (
      (flex.columns.length > 0 &&
        flex.rows.length > 0 &&
        this.frozenColumns !== frozen) ||
      this.firstRun === undefined ||
      force
    ) {
      this.firstRun = false;
      this.frozenColumns = this.flex.frozenColumns;
      left =
        frozen > 0
          ? flex.getCellBoundingRect(
              0,
              Math.min(frozen, flex.columns.length) - 1,
            ).right
          : flex.getCellBoundingRect(0, 0).left;
      // $FlowFixMe
      left -= div.parentElement.getBoundingClientRect().left;
      // eslint-disable-next-line
      div.style.left = `${left}px`;
    }
  }
}
